import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DeleteIcon } from "../icons";
import "../lists/ListRowActions.scss";
import { PicklistField } from "../blocks/fields/PicklistField";
import {
  getOperatorOptions,
  shouldClearValue,
} from "./advancedSearchRules";
import { RuleValueEditor } from "./RuleValueEditor";
import { RuleTreeProps } from "./RuleTree.types";

/** The key the validation errors and the ticked rules are held under. */
export const pathKey = (path: number[]) => path.join("-");
export const errorKey = (path: number[]) => `advancedFilters${pathKey(path)}`;

/** The rules and groups at one level, each group drawing its own level beneath it. */
export function RuleTree({
  nodes,
  parentPath = [],
  errors,
  selected,
  onSelect,
  onChange,
  onRemove,
  onUngroup,
}: RuleTreeProps) {
  return (
    <>
      {nodes.map((node, index) => {
        const path = [...parentPath, index];
        const key = pathKey(path);
        const checkbox = (
          <input
            className="form-check-input cursor-pointer"
            type="checkbox"
            aria-label={
              node.nodeType === "Group" ? "Select group" : `Select ${node.label}`
            }
            checked={selected.includes(key)}
            onChange={() => onSelect(path)}
          />
        );

        if (node.nodeType === "Group") {
          return (
            <div key={key} className="d-flex gap-3 align-items-stretch mb-3">
              <div className="d-flex align-items-center">{checkbox}</div>
              <div className="bg-light d-flex flex-column p-2">
                <button
                  type="button"
                  className="list-row-action hover-text-danger"
                  title="Ungroup"
                  aria-label="Ungroup"
                  onClick={() => onUngroup(path)}
                >
                  <FontAwesomeIcon icon={DeleteIcon} />
                </button>
                <div className="flex-grow-1 d-flex align-items-center">
                  <strong className="text-uppercase">{node.groupType}</strong>
                </div>
              </div>
              <div className="flex-grow-1">
                <RuleTree
                  {...{ errors, selected, onSelect, onChange, onRemove, onUngroup }}
                  nodes={node.children}
                  parentPath={path}
                />
              </div>
            </div>
          );
        }

        const id = `advancedFilters${key}`;
        const operatorOptions = getOperatorOptions(node.type);

        return (
          <div key={key} className="d-flex gap-3 align-items-start mb-3">
            <div className="mt-4 pt-1">{checkbox}</div>
            {node.type !== "datetime" && (
              <div className="flex-shrink-0" style={{ width: "15rem" }}>
                <PicklistField
                  label={node.label}
                  name={`${id}-operator`}
                  value={node.operator}
                  options={operatorOptions}
                  hidePlaceholder
                  onChange={(operator) =>
                    onChange(path, {
                      ...node,
                      operator: operator ?? node.operator,
                      value:
                        operator && shouldClearValue(node.operator, operator)
                          ? ""
                          : node.value,
                    })
                  }
                />
              </div>
            )}
            <div className="flex-grow-1">
              <RuleValueEditor
                rule={node}
                id={id}
                error={errors[errorKey(path)]}
                onChange={(value) => onChange(path, { ...node, value })}
              />
            </div>
            <button
              type="button"
              className="list-row-action mt-4 hover-text-danger"
              title={`Remove ${node.label}`}
              aria-label={`Remove ${node.label}`}
              onClick={() => onRemove(path)}
            >
              <FontAwesomeIcon icon={DeleteIcon} />
            </button>
          </div>
        );
      })}
    </>
  );
}
